import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Star } from "lucide-react";
import { format } from "date-fns";

interface HighlightedClientNotesProps {
  clientId: string;
}

type ClientNote = {
  id: string;
  content: string;
  created_at: string;
  reminder_at: string | null;
  highlighted: boolean;
};

export default function HighlightedClientNotes({ clientId }: HighlightedClientNotesProps) {
  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["client-notes-highlighted", clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client_notes")
        .select("*")
        .eq("client_id", clientId)
        .eq("highlighted", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as ClientNote[];
    },
    enabled: !!clientId,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Star className="h-5 w-5" /> Highlighted Notes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Φόρτωση...</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-muted-foreground">Δεν υπάρχουν highlighted σημειώσεις.</div>
        ) : (
          notes.map((n) => (
            <div key={n.id} className="p-4 rounded-lg border border-primary/50 bg-primary/5">
              <div className="flex justify-between items-start gap-4">
                <div className="whitespace-pre-wrap text-sm">{n.content}</div>
                <div className="text-xs text-muted-foreground text-right min-w-[160px]">
                  <div>{format(new Date(n.created_at), "MMM dd, yyyy")}</div>
                  {n.reminder_at && (
                    <div className="mt-1">
                      <span className="font-medium">Reminder:</span> {format(new Date(n.reminder_at), "PPP")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
